import { Caption } from '@components/utils/text';
import { ADDITIONAL_PRODUCT } from '@constants';
import { css, StyledProps, Theme, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { AdditionalProductOutboundLinkPayload, TrackEvent } from '@enums';
import {
    AdditionalProductFragment,
    VariantFragment,
} from '@graphql/generated/graphql';
import { useDisclosure } from '@hooks';
import { useAnalytics } from '@lib/analytics/analyticsContext';
import { useCompanyConfig } from '@lib/config/hooks';
import { useEmbedContext } from '@lib/embed/providers/EmbedProvider';
import StrapiImage from '@lib/image/strapiImage';
import { getStrapiAssetUrl } from '@lib/strapi';
import { Button, Card, CardProps, HStack, lineClamp, VStack } from '@lib/uikit';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo } from 'react';

import { AdditionalProductDetailTray } from './AdditionalProductDetailTray';

const SCard = styled(Card)`
    width: 100%;
    height: 100%;
    align-content: center;
    pointer-events: all;
`;

const SVStack = styled(VStack)`
    flex: 1;
    justify-content: space-between;
    padding-block: ${({ theme }) => theme.spacing.xxs};
`;

const imageStyle = ({ theme }: { theme: Theme }) => css`
    display: flex;
    width: 5rem;
    height: 5rem;
    object-fit: cover;
    border-radius: ${theme.shape.radius.sm};
`;

const SStrapiImage = styled(StrapiImage)`
    ${imageStyle}
`;

const StyledImage = styled.img`
    ${imageStyle}
`;

const Name = styled.p<StyledProps<{ color: string }>>`
    color: ${({ color }) => color};
    font: ${({ theme }) => theme.fonts.subtitle5};
    line-height: 1.2;
    font-weight: 900;
    ${lineClamp(1)}
`;

const Price = styled(Name)`
    font-weight: 500;
`;

const Description = styled(Caption)`
    font-size: 0.875rem;
    ${lineClamp(1)}
`;

interface Props extends CardProps {
    additionalProduct: AdditionalProductFragment;
    defaultSelectedVariant?: VariantFragment;
    context: AdditionalProductOutboundLinkPayload['context'];
    onDetailClose?: () => void;
    onDetailOpen?: () => void;
}

export const AdditionalProductBanner = ({
    additionalProduct,
    defaultSelectedVariant,
    context,
    onDetailClose,
    onDetailOpen,
    ...cardProps
}: Props) => {
    const router = useRouter();
    const { track } = useAnalytics();
    const { palette } = useTheme();
    const { t } = useTranslation(ADDITIONAL_PRODUCT, { keyPrefix: 'actions' });
    const { isEmbed } = useEmbedContext();
    const color = useMemo(
        () =>
            cardProps.variant === 'blurred'
                ? palette.common.white
                : palette.common.black,
        [cardProps.variant, palette.common.black, palette.common.white]
    );
    const {
        isOpen: isDetailOpen,
        onClose: closeDetail,
        onOpen: openDetail,
    } = useDisclosure();
    const { additionalProduct: additionalProductRenderOptions } =
        useCompanyConfig();

    const handleDetailOpen = useCallback(() => {
        openDetail();
        track(TrackEvent.AdditionalProductDetailOpen, {
            additional_product_id: additionalProduct.id,
        });
        onDetailOpen?.();
    }, [additionalProduct, onDetailOpen, openDetail, track]);

    const handleDetailClose = useCallback(() => {
        closeDetail();
        onDetailClose?.();
    }, [closeDetail, onDetailClose]);

    useEffect(() => {
        if (
            isEmbed &&
            router.isReady &&
            router.query.additional_product &&
            router.query.additional_product === additionalProduct.id
        ) {
            handleDetailOpen();
        }
    }, [isEmbed, router.isReady]);

    if (!additionalProduct.Url) return null;

    return (
        <>
            <SCard
                {...cardProps}
                cursor='pointer'
                padding='xxs'
                onClick={handleDetailOpen}
            >
                <HStack spacing='sm' alignItems='stretch'>
                    {additionalProduct.ImageUrl ? (
                        <StyledImage
                            src={additionalProduct.ImageUrl}
                            alt={additionalProduct.Name}
                        />
                    ) : additionalProduct.Image ? (
                        <SStrapiImage
                            sizes='5rem'
                            src={getStrapiAssetUrl(additionalProduct.Image)}
                            formats={additionalProduct.Image.formats}
                            alt={
                                additionalProduct.Image.alternativeText ??
                                additionalProduct.Name
                            }
                        />
                    ) : null}
                    <SVStack spacing='xxxs'>
                        <VStack spacing='xxxs'>
                            <Name color={color}>{additionalProduct.Name}</Name>
                            {additionalProduct.Price && (
                                <Price color={color}>
                                    {additionalProduct.Price}
                                </Price>
                            )}
                            {additionalProductRenderOptions.withDescription
                                .inPreview &&
                                additionalProduct.Description && (
                                    <Description color={color}>
                                        {additionalProduct.Description}
                                    </Description>
                                )}
                        </VStack>
                        <Button buttonSize='xsmall' isFullWidth>
                            {t('add_to_cart.label')}
                        </Button>
                    </SVStack>
                </HStack>
            </SCard>
            <AdditionalProductDetailTray
                additionalProduct={additionalProduct}
                defaultSelectedVariant={defaultSelectedVariant}
                context={context}
                isOpen={isDetailOpen}
                onClose={handleDetailClose}
            />
        </>
    );
};
